import * as T from "three";
import { drawTitleCanvas } from "./titleTexture";

export const PART_NAMES = ["Door leaf", "Frame", "Hinges", "Lockset", "Closer", "Kick plate", "Threshold"];

export const SPECS = [
  { en: "DOOR LEAF", sec: "08 11 13", tags: ["Product Data", "Shop Drawings"], anchor: [-0.2, 1.5, 0.04], side: 0, slot: 1 },
  { en: "FRAME", sec: "08 11 13", tags: ["Product Data", "Shop Drawings"], anchor: [-0.56, 2.15, 0.04], side: 0, slot: 0 },
  { en: "HINGES", sec: "08 71 00", tags: ["Product Data", "Hardware Schedule"], anchor: [-0.54, 1.19, 0.05], side: 0, slot: 2 },
  { en: "LOCKSET", sec: "08 71 00", tags: ["Product Data", "Hardware Schedule"], anchor: [0.41, 1.17, 0.09], side: 1, slot: 1 },
  { en: "CLOSER", sec: "08 71 00", tags: ["Product Data", "Hardware Schedule", "Mounting Template"], anchor: [0.23, 2.2, 0.09], side: 1, slot: 0 },
  { en: "KICK PLATE", sec: "08 71 00", tags: ["Product Data", "Hardware Schedule"], anchor: [0.2, 0.24, 0.03], side: 1, slot: 2 },
  { en: "THRESHOLD", sec: "08 71 00", tags: ["Product Data", "Hardware Schedule", "Profile Detail"], anchor: [-0.2, 0.04, 0.05], side: 0, slot: 3 },
  { en: "VISION PANEL", group: 0, sec: "08 80 00", tags: ["Product Data", "Glazing Details"], anchor: [-0.235, 1.8, 0.035], side: 0, slot: 4 },
  { en: "SEALS", group: 1, sec: "08 71 00", tags: ["Product Data", "Hardware Schedule"], anchor: [-0.525, 0.75, -0.017], side: 1, slot: 3 },
];

const OFFSETS = [[0, 0, 0.22], [-0.22, 0, -0.23], [-0.55, 0, 0.15], [0.56, 0, 0.38], [0.2, 0.35, 0.3], [0, 0.02, 0.7], [0, 0.01, 0.38]];
const TOUR_ORDER = [1, 0, 2, 3, 4, 5, 6];
const LOOP = 30;
const easeInOutCubic = (x) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
// Loop: hold assembled → eased explode → 7 highlights → eased reassemble → hold (end state == start state).
const timeline = (t) => {
  if (t < 2.5) return [0, -1];
  if (t < 5) return [easeInOutCubic((t - 2.5) / 2.5), -1];
  if (t < 23.2) return [1, TOUR_ORDER[Math.min(6, Math.floor((t - 5) / 2.6))]];
  if (t < 25.7) return [1 - easeInOutCubic((t - 23.2) / 2.5), -1];
  return [0, -1];
};
const SVG_NS = "http://www.w3.org/2000/svg";
const CLICK_PX = 5, DOUBLE_MS = 300, TOGGLE_S = 1.2;
// Intro (≤2s): door opens, title travels out from behind it while the door explodes, then hands off to the DOM h1.
export const INTRO = { openEnd: 0.7, travelStart: 0.5, travelEnd: 1.7, explodeStart: 0.75, explodeEnd: 1.85, end: 2.0 };
const TITLE_START = [0, 1.3, -0.2];
const TITLE_START_WIDTH = 0.6;
const SWING_GROUPS = [0, 3, 4, 5];
const easeOutCubic = (x) => 1 - Math.pow(1 - x, 3);
const span = (t, a, b) => Math.max(0, Math.min(1, (t - a) / (b - a)));
const lerp = (a, b, k) => a + (b - a) * k;
const introState = (t) => {
  const open = easeInOutCubic(span(t, 0, INTRO.openEnd));
  const ex = easeInOutCubic(span(t, INTRO.explodeStart, INTRO.explodeEnd));
  return { opening: open * (1 - ex), exp: ex, p: span(t, INTRO.travelStart, INTRO.travelEnd) };
};
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const pad = (i) => String(i + 1).padStart(2, "0");

export function webglAvailable() {
  try {
    const c = document.createElement("canvas");
    return !!(window.WebGLRenderingContext && (c.getContext("webgl2") || c.getContext("webgl")));
  } catch (e) {
    return false;
  }
}

function noiseTexture(brushed) {
  const c = document.createElement("canvas");
  c.width = c.height = 512;
  const ctx = c.getContext("2d");
  const im = ctx.createImageData(512, 512);
  for (let y = 0; y < 512; y++) {
    const stripe = Math.random() * 22;
    for (let x = 0; x < 512; x++) {
      const a = (y * 512 + x) * 4;
      const v = 128 + (brushed ? stripe : 0) + (Math.random() - 0.5) * (brushed ? 12 : 40);
      im.data[a] = im.data[a + 1] = im.data[a + 2] = v;
      im.data[a + 3] = 255;
    }
  }
  ctx.putImageData(im, 0, 0);
  const t = new T.CanvasTexture(c);
  t.wrapS = t.wrapT = T.RepeatWrapping;
  t.repeat.set(brushed ? 2 : 6, brushed ? 6 : 12);
  return t;
}

function rounded(w, h, d, r = 0.006) {
  r = Math.min(r, w / 3, h / 3, d / 3);
  const s = new T.Shape();
  const x = -w / 2 + r, y = -h / 2 + r, W = w - 2 * r, H = h - 2 * r;
  s.moveTo(x, y); s.lineTo(x + W, y); s.lineTo(x + W, y + H); s.lineTo(x, y + H); s.closePath();
  const geo = new T.ExtrudeGeometry(s, { depth: d - 2 * r, bevelEnabled: true, bevelSegments: 3, steps: 1, bevelSize: r, bevelThickness: r, curveSegments: 3 });
  geo.translate(0, 0, -d / 2 + r);
  geo.computeVertexNormals();
  return geo;
}

function buildEnvironment(renderer) {
  const studio = new T.Scene();
  studio.background = new T.Color("#b9bdb7");
  const softbox = (x, y, z, w, h, intensity) => {
    const m = new T.Mesh(new T.PlaneGeometry(w, h), new T.MeshBasicMaterial({ color: new T.Color().setScalar(intensity), side: T.DoubleSide }));
    m.position.set(x, y, z);
    m.lookAt(0, 1, 0);
    studio.add(m);
  };
  softbox(-3, 4, 4, 3, 6, 4); softbox(4, 3, 2, 2, 5, 2.6); softbox(0, 5, -3, 5, 2, 3); softbox(-4, 1, -2, 1, 5, 0.12);
  const pm = new T.PMREMGenerator(renderer);
  const rt = pm.fromScene(studio, 0.08);
  pm.dispose();
  studio.traverse((o) => { o.geometry?.dispose(); o.material?.dispose(); });
  return rt;
}

function addLights(scene) {
  scene.add(new T.HemisphereLight(0xf5f6ed, 0x626859, 0.65));
  const key = new T.DirectionalLight(0xfff9ec, 2.2);
  key.position.set(-3, 6, 5);
  key.castShadow = true;
  key.shadow.mapSize.set(2048, 2048);
  Object.assign(key.shadow.camera, { left: -4, right: 4, top: 5, bottom: -4, near: 0.5, far: 18 });
  key.shadow.normalBias = 0.015; key.shadow.bias = -0.0002; key.shadow.radius = 4;
  scene.add(key);
  const fill = new T.DirectionalLight(0xe4ecf4, 1.3);
  fill.position.set(4, 3, -2);
  scene.add(fill);
}

function addGround(scene) {
  const floor = new T.Mesh(new T.PlaneGeometry(200, 200), new T.MeshStandardMaterial({ color: 0xf5f5ee, roughness: 0.84 }));
  floor.rotation.x = -Math.PI / 2; floor.position.y = -0.02; floor.receiveShadow = true;
  scene.add(floor);
  const cv = document.createElement("canvas");
  cv.width = cv.height = 256;
  const cx = cv.getContext("2d");
  const gr = cx.createRadialGradient(128, 128, 0, 128, 128, 128);
  gr.addColorStop(0, "rgba(40,47,32,.36)"); gr.addColorStop(1, "rgba(40,47,32,0)");
  cx.fillStyle = gr; cx.fillRect(0, 0, 256, 256);
  const shadow = new T.Mesh(new T.PlaneGeometry(2.8, 1.5), new T.MeshBasicMaterial({ map: new T.CanvasTexture(cv), transparent: true, depthWrite: false }));
  shadow.rotation.x = -Math.PI / 2; shadow.position.y = 0.001;
  scene.add(shadow);
}

export const YC_PATHS = [
  ["M47.9985 47.9994H0V8.61853e-07H47.9985V47.9994Z", "#FF6600"],
  ["M13.9012 11.7843H17.6595L22.4961 21.5325C23.203 22.9836 23.7984 24.3976 23.7984 24.3976C23.7984 24.3976 24.4313 23.021 25.175 21.5325L30.0868 11.7843H33.5843L25.2865 27.3746V37.309H22.1244V27.1884L13.9012 11.7843Z", "#FFFFFF"],
];
export const YC_LOGO = { x: 0.215, y: 1.6, size: 0.38 };

// Y Combinator mark on the leaf's front face, right of the vision panel; unlit so the orange stays #FF6600.
function ycLogo() {
  const c = document.createElement("canvas");
  c.width = c.height = 1024;
  const ctx = c.getContext("2d");
  ctx.scale(1024 / 48, 1024 / 48);
  YC_PATHS.forEach(([d, fill]) => { ctx.fillStyle = fill; ctx.fill(new Path2D(d)); });
  const tex = new T.CanvasTexture(c);
  tex.colorSpace = T.SRGBColorSpace;
  tex.anisotropy = 8;
  const mat = new T.MeshBasicMaterial({ map: tex, toneMapped: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
  const m = new T.Mesh(new T.PlaneGeometry(YC_LOGO.size, YC_LOGO.size), mat);
  m.position.set(YC_LOGO.x, YC_LOGO.y, 0.0246);
  m.name = "yc-logo";
  return m;
}

function buildDoor(groups, textures) {
  const [grain, brush] = textures;
  const paint = new T.MeshPhysicalMaterial({ color: 0xff6600, metalness: 0.12, roughness: 0.32, clearcoat: 0.35, clearcoatRoughness: 0.28, bumpMap: grain, bumpScale: 0.00035 });
  const frameMat = paint.clone(); frameMat.color.set(0x30302e);
  const steel = new T.MeshPhysicalMaterial({ color: 0xbec3c0, metalness: 1, roughness: 0.3, anisotropy: 0.65, anisotropyRotation: Math.PI / 2, bumpMap: brush, bumpScale: 0.0002 });
  const chrome = new T.MeshStandardMaterial({ color: 0xd3d6d4, metalness: 1, roughness: 0.16 });
  const dark = new T.MeshStandardMaterial({ color: 0x333330, metalness: 0.3, roughness: 0.55 });
  const rubber = new T.MeshStandardMaterial({ color: 0x30302e, roughness: 0.9 });
  const glass = new T.MeshPhysicalMaterial({ color: 0xcbd8d2, metalness: 0, roughness: 0.08, transmission: 0.94, thickness: 0.009, ior: 1.5, transparent: true, opacity: 1, envMapIntensity: 1.2 });

  const box = (g, x, y, z, w, h, d, mat = steel, r = 0.004) => {
    const m = new T.Mesh(rounded(w, h, d, r), mat);
    m.position.set(x, y, z); m.castShadow = m.receiveShadow = true; g.add(m); return m;
  };
  const cyl = (g, x, y, z, r, len, mat = steel, axis = "y") => {
    const m = new T.Mesh(new T.CylinderGeometry(r, r, len, 32), mat);
    if (axis === "z") m.rotation.x = Math.PI / 2;
    if (axis === "x") m.rotation.z = Math.PI / 2;
    m.position.set(x, y, z); m.castShadow = m.receiveShadow = true; g.add(m); return m;
  };
  const screw = (g, x, y, z) => { cyl(g, x, y, z, 0.005, 0.0025, chrome, "z"); box(g, x, y, z + 0.002, 0.006, 0.0009, 0.001, dark, 0.0001); };
  const bar = (g, a, b, r = 0.008) => {
    const A = new T.Vector3(...a), B = new T.Vector3(...b), v = B.clone().sub(A);
    const m = new T.Mesh(new T.CylinderGeometry(r, r, v.length(), 20), steel);
    m.position.copy(A.add(B).multiplyScalar(0.5));
    m.quaternion.setFromUnitVectors(new T.Vector3(0, 1, 0), v.normalize());
    m.castShadow = true; g.add(m);
  };
  const [leaf, frame, hinges, lockset, closer, kick, threshold] = groups;

  // Door leaf: four panels around a real glazed opening.
  box(leaf, -0.4425, 1.19, 0, 0.155, 2.26, 0.048, paint, 0.002);
  box(leaf, 0.2075, 1.19, 0, 0.625, 2.26, 0.048, paint, 0.002);
  box(leaf, -0.235, 0.605, 0, 0.26, 1.09, 0.048, paint, 0.001);
  box(leaf, -0.235, 2.185, 0, 0.26, 0.27, 0.048, paint, 0.001);
  box(leaf, -0.235, 1.6, 0, 0.25, 0.89, 0.009, glass, 0.001);
  for (const z of [-0.029, 0.029]) {
    for (const x of [-0.371, -0.099]) {
      box(leaf, x, 1.6, z, 0.018, 0.934, 0.013, steel, 0.002);
      box(leaf, x + (x < -0.2 ? 0.011 : -0.011), 1.6, z * 0.7, 0.006, 0.901, 0.006, rubber, 0.001);
    }
    for (const y of [1.14, 2.06]) box(leaf, -0.235, y, z, 0.29, 0.018, 0.013, steel, 0.002);
    for (const x of [-0.371, -0.099]) for (const y of [1.18, 2.02]) screw(leaf, x, y, z + (z > 0 ? 0.009 : -0.009));
  }
  box(leaf, 0.517, 1.13, 0, 0.003, 0.23, 0.035, steel, 0.0005);
  box(leaf, 0, 2.321, 0, 1.02, 0.002, 0.041, steel, 0.0005);
  leaf.add(ycLogo());

  for (const x of [-0.562, 0.562]) {
    box(frame, x, 1.21, -0.018, 0.072, 2.42, 0.15, frameMat);
    box(frame, x + Math.sign(x) * -0.024, 1.21, -0.042, 0.025, 2.33, 0.035, frameMat);
    box(frame, x + Math.sign(x) * -0.036, 1.21, -0.017, 0.009, 2.32, 0.008, rubber, 0.001);
  }
  box(frame, 0, 2.384, -0.018, 1.055, 0.072, 0.15, frameMat);
  box(frame, 0, 2.35, -0.042, 1.05, 0.025, 0.035, frameMat);
  box(frame, 0.528, 1.13, 0.005, 0.007, 0.19, 0.046, steel);
  for (const y of [1.06, 1.2]) screw(frame, 0.535, y, 0.03);

  for (const y of [0.35, 1.19, 2.02]) {
    box(hinges, -0.516, y, 0.03, 0.065, 0.102, 0.004, steel, 0.001);
    for (let j = 0; j < 5; j++) cyl(hinges, -0.537, y - 0.041 + j * 0.0205, 0.037, 0.009, 0.019, chrome);
    for (const xx of [-0.5, -0.521]) for (const yy of [-0.032, 0.032]) screw(hinges, xx, y + yy, 0.034);
    cyl(hinges, -0.537, y + 0.053, 0.037, 0.01, 0.004, chrome);
  }

  for (const s of [1, -1]) {
    cyl(lockset, 0.411, 1.17, s * 0.031, 0.034, 0.012, steel, "z");
    cyl(lockset, 0.411, 1.17, s * 0.06, 0.012, 0.055, chrome, "z");
    cyl(lockset, 0.345, 1.17, s * 0.087, 0.011, 0.15, steel, "x");
    cyl(lockset, 0.273, 1.17, s * 0.081, 0.011, 0.022, chrome, "z");
    cyl(lockset, 0.411, 1.08, s * 0.029, 0.021, 0.009, steel, "z");
    box(lockset, 0.411, 1.08, s * 0.035, 0.003, 0.014, 0.002, dark, 0.0003);
  }

  box(closer, 0.23, 2.2, 0.06, 0.3, 0.072, 0.065, steel, 0.012);
  for (const x of [0.1, 0.36]) screw(closer, x, 2.2, 0.095);
  bar(closer, [0.16, 2.245, 0.065], [-0.02, 2.32, 0.19]);
  bar(closer, [-0.02, 2.32, 0.19], [0.25, 2.37, 0.075]);
  cyl(closer, -0.02, 2.32, 0.19, 0.014, 0.014, chrome);
  box(closer, 0.25, 2.37, 0.07, 0.09, 0.025, 0.02, steel);

  for (const s of [1, -1]) {
    box(kick, 0, 0.24, s * 0.026, 0.98, 0.32, 0.003, steel, 0.001);
    for (const x of [-0.46, 0, 0.46]) for (const y of [0.1, 0.38]) screw(kick, x, y, s * 0.029);
  }
  box(threshold, 0, 0.025, 0.005, 1.065, 0.038, 0.19, steel, 0.009);
  for (const z of [-0.06, -0.03, 0, 0.03, 0.06]) box(threshold, 0, 0.045, z, 1.02, 0.0018, 0.003, dark, 0.0003);

  // Mortise latch, frame silencers, closer adjustment screws and threshold fixings.
  box(lockset, 0.524, 1.17, 0, 0.015, 0.026, 0.025, chrome, 0.002);
  for (const y of [0.65, 1.55, 2.18]) cyl(frame, 0.524, y, -0.012, 0.006, 0.006, rubber, "x");
  for (const x of [0.13, 0.18]) cyl(closer, x, 2.16, 0.069, 0.005, 0.003, dark);
  for (const x of [-0.46, 0, 0.46]) cyl(threshold, x, 0.046, 0.065, 0.005, 0.002, chrome);
}

function buildCallouts(labels, leaders) {
  const els = SPECS.map((s, i) => {
    const wrap = document.createElement("div");
    wrap.className = "cd-callout-wrap";
    const e = document.createElement("article");
    e.className = "cd-callout";
    e.dataset.testid = `hero-callout-${pad(i)}`;
    e.innerHTML = `<header><span class="num">${pad(i)}</span><span class="name">${s.en}</span><span class="sec">${s.sec}</span></header><div class="tags">${s.tags.join(" · ")}</div>`;
    wrap.appendChild(e);
    labels.appendChild(wrap);
    return { wrap, e, pos: "", width: "", show: null, focus: null };
  });
  const lines = SPECS.map(() => {
    const g = document.createElementNS(SVG_NS, "g");
    const p = document.createElementNS(SVG_NS, "path");
    const c = document.createElementNS(SVG_NS, "circle");
    c.setAttribute("r", "3");
    g.append(p, c);
    leaders.appendChild(g);
    return { g, p, c };
  });
  return { els, lines };
}

export function createDoorScene({ stage, mount, labels, leaders, reduced, intro, region }) {
  const renderer = new T.WebGLRenderer({ antialias: true, alpha: false });
  const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
  renderer.setPixelRatio(dpr);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = T.PCFSoftShadowMap;
  renderer.toneMapping = T.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 0.92;
  const el = renderer.domElement;
  el.dataset.testid = "hero-3d-canvas";
  mount.appendChild(el);

  const scene = new T.Scene();
  scene.background = new T.Color("#f5f5ee");
  scene.fog = new T.Fog("#f5f5ee", 12, 30);
  const camera = new T.PerspectiveCamera(34, 1, 0.05, 60);
  const envRT = buildEnvironment(renderer);
  scene.environment = envRT.texture;
  addLights(scene);
  addGround(scene);

  const textures = [noiseTexture(false), noiseTexture(true)];
  const root = new T.Group();
  scene.add(root);
  const groups = PART_NAMES.map((n, i) => { const g = new T.Group(); g.userData.index = i; root.add(g); return g; });
  buildDoor(groups, textures);
  const offsets = OFFSETS.map((o) => new T.Vector3(...o));
  const { els, lines } = buildCallouts(labels, leaders);

  const focus = new T.Vector3(0, 1.15, 0);
  let W = 1, H = 1, compact = false, distance = 5.9;
  const R = { x: 0, y: 0, w: 1, h: 1 };
  let exp = reduced ? 1 : 0, selected = -1, tourTime = 0, swayT = 0;
  let pitch = 0.12, userYaw = 0, yaw = 0.35;
  let introT = intro && !reduced && !intro.isLanded() ? 0 : null, opening = 0, warmFrames = 0, handedOff = false, disposed = false;
  let title = null;
  let touring = !reduced, toggle = null;

  if (introT !== null) {
    (document.fonts?.ready ?? Promise.resolve()).then(() => {
      const el = intro.titleEl();
      if (disposed || !el || introT === null) return;
      const { canvas, aspect } = drawTitleCanvas(el, Math.max(2, dpr));
      const tex = new T.CanvasTexture(canvas);
      tex.colorSpace = T.SRGBColorSpace;
      tex.anisotropy = 4;
      const mat = new T.MeshBasicMaterial({ map: tex, transparent: true, toneMapped: false, fog: false, depthWrite: false });
      const mesh = new T.Mesh(new T.PlaneGeometry(1, 1), mat);
      mesh.renderOrder = 10;
      scene.add(mesh);
      title = { mesh, aspect };
    }).catch(() => intro.land());
  }

  const layout = () => {
    W = Math.max(1, stage.clientWidth);
    H = Math.max(1, stage.clientHeight);
    const sr = stage.getBoundingClientRect();
    const rr = region?.getBoundingClientRect() ?? sr;
    Object.assign(R, { x: rr.left - sr.left, y: rr.top - sr.top, w: Math.max(1, rr.width), h: Math.max(1, rr.height) });
    compact = R.w < 640;
    renderer.setSize(W, H);
    camera.aspect = R.w / R.h;
    camera.setViewOffset(R.w, R.h, -R.x, -R.y, W, H);
    distance = 6.3 * Math.max(1, 1.0 / camera.aspect) * (compact ? 0.92 : 1);
    camera.updateProjectionMatrix();
  };

  const project = new T.Vector3();
  const annotate = () => {
    root.updateMatrixWorld(true);
    camera.updateMatrixWorld(true);
    const width = compact ? Math.min(300, R.w - 24) : 176;
    const margin = compact ? 12 : Math.max(12, R.w * 0.02);
    const top = R.y + Math.max(44, R.h * 0.09);
    const bottom = R.y + R.h - Math.max(60, R.h * 0.1);
    const step = clamp((bottom - top) / 5, 48, 100);
    SPECS.forEach((s, i) => {
      const gi = s.group ?? i;
      const v = groups[gi].localToWorld(project.set(...s.anchor)).project(camera);
      const x = (v.x * 0.5 + 0.5) * W, y = (-v.y * 0.5 + 0.5) * H;
      const onScreen = v.z < 1 && v.z > -1 && introT === null;
      const show = compact ? onScreen && i < 7 && selected === i : onScreen && (exp > 0.35 || selected === i);
      const lx = compact ? R.x + margin : s.side ? R.x + R.w - margin - width : R.x + margin;
      const ly = compact ? R.y + R.h - 62 : top + s.slot * step;
      const c = els[i];
      const w = `${width}px`, pos = `translate3d(${Math.round(lx)}px, ${Math.round(ly)}px, 0)`, focus = selected === gi;
      if (c.width !== w) { c.wrap.style.width = w; c.width = w; }
      if (c.pos !== pos) { c.wrap.style.transform = pos; c.pos = pos; }
      if (c.show !== show) { c.e.classList.toggle("visible", show); lines[i].g.style.opacity = show ? "1" : "0"; c.show = show; }
      if (c.focus !== focus) { c.e.classList.toggle("focus", focus); c.focus = focus; }
      const endX = compact ? lx + 28 : s.side ? lx : lx + width;
      const endY = compact ? ly : ly + 14;
      const elbow = compact ? endX : endX + (s.side ? -16 : 16);
      lines[i].p.setAttribute("d", `M ${x} ${y} L ${elbow} ${endY} L ${endX} ${endY}`);
      lines[i].c.setAttribute("cx", x);
      lines[i].c.setAttribute("cy", y);
    });
  };

  const tmpDir = new T.Vector3(), tmpFwd = new T.Vector3(), startWorld = new T.Vector3(...TITLE_START);
  const screenToWorld = (px, py, depth) => {
    const dir = tmpDir.set((px / W) * 2 - 1, -(py / H) * 2 + 1, 0.5).unproject(camera).sub(camera.position).normalize();
    camera.getWorldDirection(tmpFwd);
    return camera.position.clone().addScaledVector(dir, depth / dir.dot(tmpFwd));
  };

  // Billboarded plane: starts behind the closed leaf (depth-occluded), ends exactly over the DOM h1's rect.
  const placeTitle = (p) => {
    const tr = intro.titleEl()?.getBoundingClientRect();
    if (!tr || tr.width < 1) return;
    const sr = stage.getBoundingClientRect();
    const l = tr.left - sr.left, t = tr.top - sr.top, cy = t + tr.height / 2, dEnd = distance * 0.55;
    const endC = screenToWorld(l + tr.width / 2, cy, dEnd);
    const endW = screenToWorld(l, cy, dEnd).distanceTo(screenToWorld(l + tr.width, cy, dEnd));
    const s = startWorld.clone().applyMatrix4(camera.matrixWorldInverse);
    const e = endC.applyMatrix4(camera.matrixWorldInverse);
    const k = easeInOutCubic(p), kd = easeOutCubic(p);
    const pos = new T.Vector3(lerp(s.x, e.x, k), lerp(s.y, e.y, k), lerp(s.z, e.z, kd)).applyMatrix4(camera.matrixWorld);
    const w = lerp(TITLE_START_WIDTH, endW, k);
    title.mesh.position.copy(pos);
    title.mesh.quaternion.copy(camera.quaternion);
    title.mesh.scale.set(w, w * title.aspect, 1);
    title.mesh.material.opacity = 1 - span(introT, INTRO.travelEnd, INTRO.end - 0.05);
  };

  const finishIntro = (tourAt) => {
    introT = null; opening = 0; tourTime = tourAt; swayT = 0;
    delete stage.dataset.introT;
    if (title) title.mesh.visible = false;
    intro.land();
  };

  const tick = (dt) => {
    if (introT !== null && !handedOff && intro.isLanded()) finishIntro(exp > 0.5 ? 5 : 0);
    if (introT !== null) {
      intro.onFrame();
      if (!title || warmFrames < 2) { if (title) warmFrames += 1; }
      else introT += Math.min(dt, 1 / 30);
      ({ exp, opening } = introState(introT));
      selected = -1;
      if (!down) userYaw *= 1 - Math.min(1, dt * 0.35);
      yaw = 0.28 + userYaw;
      if (!handedOff && introT >= INTRO.travelEnd) { handedOff = true; intro.onHandoff(); }
      if (introT >= INTRO.end) finishIntro(5);
      else stage.dataset.introT = introT.toFixed(2);
    } else if (touring) {
      tourTime += dt;
      swayT += dt;
      [exp, selected] = timeline(tourTime % LOOP);
      if (!down) { userYaw *= 1 - Math.min(1, dt * 0.35); pitch += (0.12 - pitch) * Math.min(1, dt * 0.35); }
      yaw = 0.28 + Math.sin((swayT / LOOP) * Math.PI * 2) * 0.14 + userYaw;
    } else {
      if (toggle) {
        toggle.t = Math.min(1, toggle.t + dt / TOGGLE_S);
        exp = lerp(toggle.from, toggle.to, easeInOutCubic(toggle.t));
        if (toggle.t >= 1) toggle = null;
      }
      yaw = (interacted ? 0.28 : 0.35) + userYaw;
    }
    groups.forEach((g, i) => {
      g.position.copy(offsets[i]).multiplyScalar(exp);
      g.rotation.y = 0;
      if (opening > 0 && SWING_GROUPS.includes(i)) {
        const angle = -opening * 1.25;
        g.rotation.y = angle;
        g.position.x += -0.535 + Math.cos(angle) * 0.535;
        g.position.z += -Math.sin(angle) * 0.535;
      }
    });
    camera.position.set(
      focus.x + Math.sin(yaw) * Math.cos(pitch) * distance,
      focus.y + Math.sin(pitch) * distance,
      focus.z + Math.cos(yaw) * Math.cos(pitch) * distance,
    );
    camera.lookAt(focus);
    camera.updateMatrixWorld();
    if (introT !== null && title) placeTitle(introState(introT).p);
    renderer.render(scene, camera);
    annotate();
    sync();
  };

  // Visitor control: click (< CLICK_PX of travel) highlights a part; a second click within DOUBLE_MS toggles exploded/assembled.
  const ray = new T.Raycaster(), ndc = new T.Vector2(), box = new T.Box3(), corner = new T.Vector3();
  let interacted = false, lastClickAt = 0;
  const SMALL = [2, 3, 4, 5, 6];
  const screenBox = (o, r) => {
    box.setFromObject(o);
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (let k = 0; k < 8; k++) {
      corner.set(k & 1 ? box.max.x : box.min.x, k & 2 ? box.max.y : box.min.y, k & 4 ? box.max.z : box.min.z).project(camera);
      const x = r.left + (corner.x * 0.5 + 0.5) * r.width, y = r.top + (-corner.y * 0.5 + 0.5) * r.height;
      x0 = Math.min(x0, x); x1 = Math.max(x1, x); y0 = Math.min(y0, y); y1 = Math.max(y1, y);
    }
    return [x0, y0, x1, y1];
  };
  // Ray first; small hardware also wins within a few px of any of its meshes so thin parts stay easy to hit.
  const pick = (cx, cy) => {
    const r = el.getBoundingClientRect();
    ndc.set(((cx - r.left) / r.width) * 2 - 1, -((cy - r.top) / r.height) * 2 + 1);
    ray.setFromCamera(ndc, camera);
    let first = -1;
    for (const hit of ray.intersectObjects(groups, true)) {
      let o = hit.object;
      while (o && o.userData.index === undefined) o = o.parent;
      if (o) { first = o.userData.index; break; }
    }
    if (SMALL.includes(first)) return first;
    let best = -1, bestArea = Infinity;
    for (const gi of SMALL) {
      groups[gi].traverse((m) => {
        if (!m.isMesh) return;
        const [x0, y0, x1, y1] = screenBox(m, r);
        const area = (x1 - x0) * (y1 - y0);
        if (cx >= x0 - 4 && cx <= x1 + 4 && cy >= y0 - 4 && cy <= y1 + 4 && area < bestArea) { best = gi; bestArea = area; }
      });
    }
    return best >= 0 ? best : first;
  };
  const sync = () => {
    stage.dataset.part = selected < 0 ? "" : PART_NAMES[selected];
    stage.dataset.exploded = (toggle ? toggle.to : exp) > 0.5 ? "1" : "0";
  };
  const takeControl = () => {
    if (interacted) return;
    interacted = true;
    touring = false;
    selected = -1;
    userYaw = clamp(yaw - 0.28, -1.4, 1.4);
    const to = exp > 0.5 ? 1 : 0;
    toggle = reduced || exp === to ? ((exp = to), null) : { from: exp, to, t: 0 };
  };
  const toggleExploded = () => {
    const to = (toggle ? toggle.to : exp) > 0.5 ? 0 : 1;
    toggle = reduced ? ((exp = to), null) : { from: exp, to, t: 0 };
  };
  // Single click applies at once; a second click within DOUBLE_MS restores the pre-click highlight and toggles instead.
  let beforeClick = -1;
  const onClick = (cx, cy, now) => {
    if (introT !== null) {
      if (!handedOff) return;
      finishIntro(5);
    }
    if (lastClickAt && now - lastClickAt < DOUBLE_MS) {
      lastClickAt = 0;
      selected = beforeClick;
      toggleExploded();
    } else {
      lastClickAt = now;
      const part = pick(cx, cy);
      takeControl();
      beforeClick = selected;
      selected = part === selected ? -1 : part;
    }
    sync();
    if (!running) tick(0);
  };

  let down = false, lastX = 0, lastY = 0, travel = 0;
  const onDown = (e) => { down = true; travel = 0; lastX = e.clientX; lastY = e.clientY; el.setPointerCapture?.(e.pointerId); el.classList.add("dragging"); };
  const onMove = (e) => {
    if (!down) return;
    travel += Math.hypot(e.clientX - lastX, e.clientY - lastY);
    userYaw = clamp(userYaw - (e.clientX - lastX) * 0.006, -1.4, 1.4);
    pitch = clamp(pitch + (e.clientY - lastY) * 0.004, -0.1, 0.7);
    lastX = e.clientX; lastY = e.clientY;
    if (!running) tick(0);
  };
  const onUp = (e) => {
    if (down && e.type === "pointerup" && travel < CLICK_PX) onClick(e.clientX, e.clientY, e.timeStamp || performance.now());
    down = false;
    el.classList.remove("dragging");
  };
  el.addEventListener("pointerdown", onDown);
  el.addEventListener("pointermove", onMove);
  el.addEventListener("pointerup", onUp);
  el.addEventListener("pointercancel", onUp);

  const clock = new T.Clock();
  let raf = 0, running = false;
  const frame = () => { raf = requestAnimationFrame(frame); tick(Math.min(clock.getDelta(), 0.25)); };
  const start = () => { if (running) return; running = true; clock.getDelta(); raf = requestAnimationFrame(frame); };
  const stop = () => { running = false; cancelAnimationFrame(raf); };

  const ro = new ResizeObserver(() => { layout(); if (!running) tick(0); });
  // Test aid: a visible client-space point on each of the 7 components (current pose) that picks that component.
  stage.partPoints = () => {
    const r = el.getBoundingClientRect();
    const toClient = (v) => ({ x: r.left + (v.x * 0.5 + 0.5) * r.width, y: r.top + (-v.y * 0.5 + 0.5) * r.height });
    return PART_NAMES.map((name, i) => {
      const cands = [groups[i].localToWorld(project.set(...SPECS[i].anchor)).project(camera).clone()];
      groups[i].traverse((m) => { if (m.isMesh) cands.push(box.setFromObject(m).getCenter(corner).project(camera).clone()); });
      const pts = cands.map(toClient);
      const p = pts.find((q) => pick(q.x, q.y) === i) || pts[0];
      return { name, x: p.x, y: p.y, hit: PART_NAMES[pick(p.x, p.y)] };
    });
  };
  ro.observe(stage);
  if (region) ro.observe(region);
  layout();
  renderer.compile(scene, camera);
  tick(0);
  start();

  return {
    setActive: (on) => (on ? start() : stop()),
    dispose: () => {
      disposed = true;
      stop();
      ro.disconnect();
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      el.removeEventListener("pointercancel", onUp);
      scene.traverse((o) => {
        o.geometry?.dispose();
        const mats = Array.isArray(o.material) ? o.material : o.material ? [o.material] : [];
        mats.forEach((m) => { m.map?.dispose(); m.dispose(); });
      });
      textures.forEach((t) => t.dispose());
      envRT.dispose();
      renderer.dispose();
      renderer.forceContextLoss();
      el.remove();
      labels.innerHTML = "";
      leaders.innerHTML = "";
    },
  };
}
